import { ShoppingBag } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import ResponsiveImg from "@/components/ResponsiveImg";
import PhotoReveal from "@/components/PhotoReveal";
import { useLanguage } from "@/context/LanguageContext";
import { useCart, singleWhatsappUrl, whatsappConfigured } from "@/context/CartContext";
import { products, defaultSizeId, getSize, formatPrice } from "@/config/products";

export default function Collection() {
  const { t } = useLanguage();
  const { addItem } = useCart();
  const waOn = whatsappConfigured();
  const defaultSize = getSize(defaultSizeId).label;
  const reduce = useReducedMotion();

  const order = (p) =>
    window.open(singleWhatsappUrl({ productKey: p.key, size: defaultSize, qty: 1, price: p.basePrice }, t), "_blank");

  return (
    <section id="collection" data-testid="collection-section" className="bg-white py-20 lg:py-28">
      <div className="mx-auto max-w-7xl px-5 lg:px-10">
        <Reveal className="mb-12 text-center">
          <span className="eyebrow text-[11px] text-[#999]">{t.nav.bestSellers}</span>
          <h2 className="mt-3 font-serif-display text-3xl font-medium text-[#111] sm:text-4xl">
            {t.collection.title}
          </h2>
          <p className="mx-auto mt-3 max-w-md font-body text-sm text-[#777]">{t.collection.subtitle}</p>
        </Reveal>

        <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
          {products.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <article
                data-testid={`collection-card-${p.id}`}
                className="collection-card card-lift tactile-card group flex h-full flex-col overflow-hidden rounded-2xl border border-[#eee] bg-white"
              >
                <Link
                  to={`/products/${p.id}`}
                  data-testid={`collection-image-${p.id}`}
                  aria-label={`${t.products[p.key].name} — ${t.collection.viewDetails || "View details"}`}
                  className="reflect-sweep block cursor-pointer overflow-hidden bg-[#f7f7f5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#111]"
                >
                  <motion.div
                    layoutId={reduce ? undefined : `product-image-${p.id}`}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <PhotoReveal>
                      <ResponsiveImg
                        src={p.thumb}
                        alt={t.products[p.key].name}
                        widths={[400, 700, 1000]}
                        sizes="(max-width: 640px) 92vw, (max-width: 1024px) 45vw, 400px"
                        loading="lazy"
                        decoding="async"
                        fadeIn={false}
                        className="img-luxe aspect-square w-full object-cover"
                      />
                    </PhotoReveal>
                  </motion.div>
                </Link>
                <div className="flex flex-1 flex-col p-6">
                  <Link
                    to={`/products/${p.id}`}
                    className="nav-underline w-fit font-serif-display text-xl text-[#111]"
                  >
                    {t.products[p.key].name}
                  </Link>
                  <p className="mt-2 flex-1 font-body text-sm text-[#777]">{t.products[p.key].short}</p>
                  <span className="mt-4 font-serif-display text-2xl text-[#111]">{formatPrice(p.basePrice)}</span>
                  <div className="mt-5 flex gap-2">
                    <button
                      data-testid={`collection-view-${p.id}`}
                      onClick={() => addItem(p, defaultSize, p.basePrice, 1)}
                      className="press flex flex-1 items-center justify-center gap-1.5 rounded-full border border-[#ddd] px-4 py-2.5 font-body text-sm text-[#111] transition-colors duration-200 hover:border-[#111]"
                    >
                      <ShoppingBag className="h-4 w-4" /> {t.flagship.addToCart}
                    </button>
                    {waOn && (
                      <button
                        data-testid={`collection-buy-${p.id}`}
                        onClick={() => order(p)}
                        className="press flex-1 rounded-full bg-[#111] px-4 py-2.5 font-body text-sm font-medium text-white transition-colors duration-200 hover:bg-[#2a2a2a]"
                      >
                        {t.collection.buy}
                      </button>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
